import React, { useCallback, useMemo, useRef } from 'react';
import { RefreshControl, SectionList, StyleSheet } from 'react-native';

import { useScrollToTop } from '@react-navigation/native';

import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { ds } from '@/components/lib/design.system';
import { useFetchOnResume } from '@/hooks/use-fetch-resume';
import { useForceRefresh } from '@/hooks/use-force-refresh';
import { LoadingScreen } from '@/screens/LoadingScreen';
import { designSystem } from '@/styles/styles';

import { useArcadeGameResults } from '../hooks/use-arcade-game-results';
import { GameResult, GameResultItem } from './GameResultItem';

const ItemSeparator = () => <SizedBox value={12} />;

export const GameResultsSectionList = () => {
    const { data, hasNextPage, isFetchingNextPage, fetchNextPage, isLoading, refetch } = useArcadeGameResults({
        page: 1,
    });
    const allResults = useMemo(() => {
        return data?.pages?.map(page => page?.data).flat() || [];
    }, [data]);

    const { forceRefreshing, onRefresh } = useForceRefresh(refetch);
    useFetchOnResume(refetch);

    const sections = useMemo(() => {
        const groupedResults = allResults.reduce<{
            pending?: typeof allResults;
            ready?: typeof allResults;
            completed?: Record<string, typeof allResults>;
        }>((acc, item) => {
            if (item.status !== 'PENDING' && !!item.settled_at) {
                const dateKey = new Date(item.settled_at).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                });
                if (!acc.completed) {
                    acc.completed = {};
                }
                if (!acc.completed[dateKey]) {
                    acc.completed[dateKey] = [];
                }
                acc.completed[dateKey].push(item);
            } else {
                if (!acc.pending) {
                    acc.pending = [];
                }
                acc.pending.push(item);
            }
            return acc;
        }, {});

        const sectionData = [];

        // Add sections in priority order
        if (groupedResults.pending?.length) {
            sectionData.push({
                title: 'Pending',
                data: groupedResults.pending,
            });
        }

        // Add completed sections sorted by date (newest first)
        if (groupedResults.completed) {
            Object.entries(groupedResults.completed)
                .sort(([dateA], [dateB]) => new Date(dateB).getTime() - new Date(dateA).getTime())
                .forEach(([date, items]) => {
                    if (items.length > 0) {
                        sectionData.push({
                            title: date,
                            data: items,
                        });
                    }
                });
        }

        return sectionData;
    }, [allResults]);

    const renderItem = useCallback(({ item }: { item: GameResult }) => {
        return <GameResultItem gameResult={item} />;
    }, []);

    const renderSectionHeader = useCallback(({ section }: { section: { title: string } }) => {
        return (
            <Text variant="titleMedium2" color="neutral300" py="s16">
                {section.title}
            </Text>
        );
    }, []);

    const listRef = useRef<SectionList<GameResult, { title: string; data: GameResult[] }>>(null);
    useScrollToTop(listRef);

    if (isLoading) {
        return <LoadingScreen />;
    }

    return (
        <SectionList
            ref={listRef}
            sections={sections}
            renderItem={renderItem}
            renderSectionHeader={renderSectionHeader}
            stickySectionHeadersEnabled={false}
            keyExtractor={item => item.id}
            ItemSeparatorComponent={ItemSeparator}
            contentContainerStyle={styles.contentContainer}
            showsVerticalScrollIndicator={false}
            refreshControl={
                <RefreshControl
                    enabled
                    onRefresh={onRefresh}
                    refreshing={forceRefreshing}
                    colors={[designSystem.colors.neutral700]}
                    tintColor={ds.colorValue.icon.primary}
                />
            }
            onEndReachedThreshold={0.5}
            onEndReached={() => !isFetchingNextPage && hasNextPage && fetchNextPage()}
        />
    );
};

const styles = StyleSheet.create({
    contentContainer: {
        paddingBottom: 64,
    },
});
